/*
 * rs-infinite-scroll.js — Rococo Sand
 *
 * Collection pages pull in the next paginated page as the shopper reaches the
 * bottom of the grid instead of making them click through numbered pages. The
 * <div class="pagination"> markup stays in the DOM (hidden) so crawlers keep
 * their links and so we have somewhere to fall back to if a fetch fails.
 *
 * It has to share the collection page with two other custom scripts:
 *  - rs-price-filter.js empties and repaints the grid itself while rs_pmin /
 *    rs_pmax are set, and there is no server pagination left to follow then,
 *    so we stand down for those requests.
 *  - rs-filter-counts.js reads #RsSoldOutSizes by id, so each fetched page's
 *    map is merged into the one already on the page rather than appended as a
 *    second element with the same id.
 */
(function () {
  'use strict';

  if (window.rsInfiniteScroll) return;
  window.rsInfiniteScroll = true;

  var SEL = {
    root: '#CollectionAjaxContent',
    section: '[data-section-type="collection-grid"]',
    grid: '.collection-grid__wrapper .grid--uniform',
    pagination: '.pagination',
    next: '.next a[href]',
    sentinel: '[data-rs-infinite]',
    status: '[data-rs-infinite-status]',
    soldOut: '#RsSoldOutSizes',
    priceRange: '.price-range'
  };
  var HIDDEN = 'pagination--rs-hidden';
  var LOADING = 'is-loading';
  var PRICE_PARAMS = ['rs_pmin', 'rs_pmax'];
  var LOOK_AHEAD = '900px 0px';
  var RETRY_DELAY = 1500;

  var state = null;

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function supported() {
    return 'IntersectionObserver' in window &&
      typeof window.URLSearchParams === 'function' &&
      typeof window.DOMParser === 'function' &&
      typeof window.fetch === 'function';
  }

  function priceFilterActive() {
    var params = new URLSearchParams(window.location.search);
    for (var i = 0; i < PRICE_PARAMS.length; i++) {
      if (params.get(PRICE_PARAMS[i])) return true;
    }
    return false;
  }

  function absolute(href) { return new URL(href, window.location.href).href; }

  /* paginate.next.url carries whatever was on the request, so a page fetched
     through the Section Rendering API hands back links with section_id on
     them. Those are fine to fetch but must never be clicked. */
  function cleanLinks(el) {
    qsa('a[href]', el).forEach(function (link) {
      var url = new URL(link.getAttribute('href'), window.location.href);
      url.searchParams.delete('section_id');
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  function sectionRenderUrl(pageUrl, sectionId) {
    var url = new URL(pageUrl, window.location.href);
    url.searchParams.set('section_id', sectionId);
    return url.href;
  }

  function announce(s, message) {
    if (s.status) s.status.textContent = message;
  }

  function teardown(revealPagination) {
    if (!state) return;
    if (state.retry) window.clearTimeout(state.retry);
    if (state.observer) state.observer.disconnect();
    if (state.sentinel) {
      state.sentinel.classList.remove(LOADING);
      state.sentinel.hidden = true;
    }
    if (revealPagination && state.pagination) state.pagination.classList.remove(HIDDEN);
    state = null;
  }

  function setup() {
    teardown(false);
    if (!supported()) return;

    var root = qs(SEL.root);
    var section = qs(SEL.section);
    if (!root || !section) return;

    var sentinel = qs(SEL.sentinel, root);
    var grid = qs(SEL.grid, root);
    var pagination = qs(SEL.pagination, root);
    if (!sentinel || !grid || !pagination) return;

    var nextLink = qs(SEL.next, pagination);
    if (!nextLink || priceFilterActive()) {
      sentinel.hidden = true;
      return;
    }

    sentinel.hidden = false;
    pagination.classList.add(HIDDEN);

    state = {
      sectionId: section.getAttribute('data-section-id'),
      grid: grid,
      pagination: pagination,
      sentinel: sentinel,
      status: qs(SEL.status, sentinel),
      nextUrl: absolute(nextLink.getAttribute('href')),
      loading: false,
      retried: false,
      retry: null,
      observer: null
    };

    state.observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          loadNext();
          return;
        }
      }
    }, { rootMargin: LOOK_AHEAD });

    state.observer.observe(sentinel);
  }

  function appendCards(s, doc) {
    var incoming = qs(SEL.grid, doc);
    if (!incoming) return 0;

    var seen = {};
    qsa('[data-product-id]', s.grid).forEach(function (card) {
      seen[card.getAttribute('data-product-id')] = true;
    });

    var fragment = document.createDocumentFragment();
    var added = 0;

    Array.prototype.slice.call(incoming.children).forEach(function (card) {
      // Every product-grid-item render also emits a <style> and a <script>
      // sibling, identical for each card, that the first page already carries.
      if (!card.matches || !card.matches('.grid__item')) return;

      // Products shift between pages when stock or sort order changes mid-browse.
      var id = card.getAttribute('data-product-id');
      if (id) {
        if (seen[id]) return;
        seen[id] = true;
      }
      fragment.appendChild(document.importNode(card, true));
      added += 1;
    });

    if (!added) return 0;

    s.grid.appendChild(fragment);
    // Recalculates AOS offsets for the new cards and rebinds quick shop.
    if (window.theme && theme.reinitProductGridItem) theme.reinitProductGridItem();
    return added;
  }

  function mergeSoldOutSizes(doc) {
    var target = qs(SEL.soldOut);
    var incoming = qs(SEL.soldOut, doc);
    if (!target || !incoming) return;

    var current, extra;
    try { current = JSON.parse(target.textContent || '{}'); } catch (e) { current = {}; }
    try { extra = JSON.parse(incoming.textContent || '{}'); } catch (e) { return; }

    Object.keys(extra).forEach(function (handle) { current[handle] = extra[handle]; });
    target.textContent = JSON.stringify(current);
  }

  function advance(s, doc) {
    var incoming = qs(SEL.pagination, doc);

    if (incoming) {
      var replacement = document.importNode(incoming, true);
      cleanLinks(replacement);
      replacement.classList.add(HIDDEN);
      s.pagination.parentNode.replaceChild(replacement, s.pagination);
      s.pagination = replacement;
    }

    var nextLink = s.pagination && qs(SEL.next, s.pagination);
    s.nextUrl = nextLink ? absolute(nextLink.getAttribute('href')) : null;

    if (!s.nextUrl) {
      s.observer.disconnect();
      s.sentinel.hidden = true;
    }
  }

  function loadNext() {
    var s = state;
    if (!s || s.loading || !s.nextUrl) return;

    // The price slider repaints the grid without firing collection:reloaded.
    if (priceFilterActive()) return;

    s.loading = true;
    s.sentinel.classList.add(LOADING);
    announce(s, 'Loading more products');

    fetch(sectionRenderUrl(s.nextUrl, s.sectionId), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (state !== s) return; // a filter re-render replaced us mid-flight
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var added = appendCards(s, doc);
        mergeSoldOutSizes(doc);
        advance(s, doc);
        s.retried = false;
        announce(s, added + ' more products loaded');
      })
      .catch(function () {
        if (state !== s) return;
        announce(s, '');
        if (s.retried) {
          teardown(true); // hand the shopper back the real Next link
        } else {
          s.retried = true;
          s.retry = window.setTimeout(function () {
            if (state === s) loadNext();
          }, RETRY_DELAY);
        }
      })
      .then(function () {
        if (state !== s) return;
        s.loading = false;
        s.sentinel.classList.remove(LOADING);
      });
  }

  var pending = null;
  function reset(delay) {
    window.clearTimeout(pending);
    pending = window.setTimeout(setup, delay || 0);
  }

  // Fired by theme.js once a filter/sort AJAX render has swapped #CollectionAjaxContent.
  document.addEventListener('collection:reloaded', function () { reset(0); });
  window.addEventListener('popstate', function () { reset(0); });

  // rs-price-filter.js takes over (or releases) the grid on a slider change
  // without any event of its own, so re-read the URL once it has settled.
  ['change', 'input'].forEach(function (type) {
    document.addEventListener(type, function (evt) {
      if (evt.target && evt.target.closest && evt.target.closest(SEL.priceRange)) reset(400);
    }, true);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
